<template>
  <div>
    <TopNav />

    <main class="dashboard">
      <div class="dashboard-header">
        <h1 class="dashboard-title">Gerätelager</h1>
        <p class="dashboard-subtitle">
          Geräte, die beim Abschluss eines Reparaturtickets eingebucht wurden.
        </p>
      </div>

      <!-- Ein Suchfeld, das der Handscanner bedienen kann: es filtert schon beim
           Tippen, ein Scan trifft über die R2-Nummer genau ein Gerät. Es wählt
           nichts aus — alles, was ein einzelnes Gerät betrifft, steckt im
           Detail-Modal der Zeile, dort sieht man, was man vor sich hat.

           Lieferscheine werden im Dashboard erstellt (Bereich „Lieferscheine"),
           dort steht auch, was je Servicepartner vorbereitet ist. -->
      <div class="stock-toolbar">
        <div class="stock-scan">
          <label for="stock-scan-input" class="form-label">Suchen oder R2-Nummer scannen</label>
          <input
            id="stock-scan-input"
            ref="scanInput"
            v-model="searchValue"
            type="text"
            class="form-control"
            placeholder="R2-xxxx, IMEI, Gerät …"
            autocomplete="off"
            :disabled="loading"
            @keydown.enter.prevent="onScanEnter"
          />
          <div v-if="searchTerm !== ''" class="stock-scan-notice">
            {{ sortedItems.length }} von {{ visibleItems.length }}
            <button type="button" class="btn btn-link btn-sm stock-scan-clear" @click="clearSearch">
              Suche aufheben
            </button>
          </div>
        </div>
        <!-- Abgeschlossene Geräte sind nicht mehr im Lager — standardmäßig weg,
             aber auffindbar. -->
        <div class="form-check stock-show-closed">
          <input id="stock-show-closed" v-model="showClosed" type="checkbox" class="form-check-input" />
          <label for="stock-show-closed" class="form-check-label">Abgeschlossene anzeigen</label>
        </div>
        <div class="form-check stock-show-closed">
          <input id="stock-only-legacy" v-model="onlyLegacy" type="checkbox" class="form-check-input" />
          <label for="stock-only-legacy" class="form-check-label">Nur Altgeräte</label>
        </div>
      </div>

      <div v-if="loading" class="spinner" aria-label="Lädt"></div>
      <div v-else-if="loadError" class="error">{{ loadError }}</div>

      <div v-else-if="items.length === 0" class="empty-state">
        Noch kein Gerät im Lager. Geräte entstehen beim Abschließen eines Tickets.
      </div>

      <div v-else-if="sortedItems.length === 0 && searchTerm === ''" class="empty-state">
        Kein Gerät im Lager — alle {{ items.length }} Geräte sind abgeschlossen.
      </div>

      <div v-else-if="sortedItems.length === 0" class="empty-state">
        Kein Gerät passt zu „{{ searchValue.trim() }}".
        <button type="button" class="btn btn-link btn-sm" @click="clearSearch">Suche aufheben</button>
        <div v-if="hiddenClosedHits > 0" class="stock-hidden-closed">
          {{ hiddenClosedLabel }}
          <button type="button" class="btn btn-link btn-sm" @click="showClosed = true">anzeigen</button>
        </div>
      </div>

      <div v-else class="table-responsive">
        <table class="table table-sm table-striped align-middle stock-table">
          <thead>
            <tr>
              <th
                v-for="col in SORTABLE_COLUMNS"
                :key="col.key"
                scope="col"
                class="stock-sortable"
                :style="col.width ? { width: col.width } : undefined"
                :aria-sort="sortKey === col.key ? (sortAsc ? 'ascending' : 'descending') : 'none'"
                @click="sortBy(col.key)"
              >
                {{ col.label }}
                <span class="stock-sort-marker">{{ sortKey === col.key ? (sortAsc ? '▲' : '▼') : '' }}</span>
              </th>
              <th scope="col">IMEI / SN</th>
              <th scope="col" class="stock-status-cell">Status</th>
              <th scope="col" style="width: 110px;"><span class="visually-hidden">Details</span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="d in sortedItems" :key="d.id">
              <!-- Die Lagernummer ist die R2-Auftragsnummer, deshalb führt sie
                   direkt ins Ticket. „RD-…" (Lagergerät ohne Ticket) bleibt Text. -->
              <td data-label="R2-Nr" class="stock-number">
                <router-link
                  v-if="d.ticket"
                  :to="{ name: 'ticket-detail', params: { id: d.ticket.id } }"
                >{{ d.stock_number }}</router-link>
                <span v-else>{{ d.stock_number }}</span>
              </td>
              <td data-label="Hersteller">{{ d.vendor.description || '—' }}</td>
              <!-- Der Master führt in seine Verwaltung — von dort aus pflegt man
                   Rezepturen und Zubehör. Speicher und Farbe bleiben Text: sie
                   gehören zur Variante, nicht zum Master.
                   In einem neuen Tab: man schaut den Master nachschlagend an und
                   will danach in der Lagerliste weiterarbeiten, ohne die Suche
                   und die Sortierung neu setzen zu müssen. Vue Router überlässt
                   einen Link mit `target="_blank"` von sich aus dem Browser. -->
              <td data-label="Gerät">
                <!-- Altgerät: kein Master, den man öffnen könnte — nur die Bezeichnung. -->
                <template v-if="d.is_legacy_device">
                  {{ d.master.description }}
                  <span class="badge bg-secondary stock-legacy-badge">Altgerät</span>
                </template>
                <template v-else>
                <router-link
                  :to="{ name: 'masterarticle', params: { id: d.master.id } }"
                  target="_blank"
                >{{ d.master.description }}</router-link><span
                  v-if="variantLabel(d)"
                  class="stock-variant"
                >, {{ variantLabel(d) }}</span>
                </template>
              </td>
              <td data-label="Zustand">{{ d.condition ? d.condition.description : '—' }}</td>
              <td data-label="IMEI / SN" class="ticket-mono">{{ d.imei_sn || '—' }}</td>
              <!-- Status als Badge mit dem Zeitpunkt des letzten Wechsels - dieselbe
                   Machart wie in der Ticketliste. Farben aus den Stammdaten. -->
              <td data-label="Status" class="stock-status-cell">
                <span class="stock-status" :style="statusStyle(d)">
                  {{ d.status.description }}
                  <span v-if="d.status.changed_timestamp" class="stock-status-when">
                    · {{ formatDay(d.status.changed_timestamp) }}
                  </span>
                </span>
                <!-- Ergebnis beim Servicepartner - bleibt auch nach der Rückkehr sichtbar. -->
                <PartnerOutcomeBadge
                  v-if="d.partner_case && PARTNER_OUTCOME_STATUS_IDS.includes(d.status.id)"
                  class="stock-partner-outcome"
                  :outcome="d.partner_case.outcome"
                  :grading="d.partner_case.grading"
                />
              </td>
              <td data-label="Details">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary"
                  @click="onShowDetails(d)"
                >Details</button>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="hiddenClosedHits > 0" class="stock-hidden-closed">
          {{ hiddenClosedLabel }}
          <button type="button" class="btn btn-link btn-sm" @click="showClosed = true">anzeigen</button>
        </div>
      </div>
    </main>

    <AlertModal ref="alertModal" :title="alertTitle" />
    <ConfirmModal ref="confirmModal" :title="confirmTitle" :confirm-label="confirmLabel" />
    <StockDeviceDetailsModal ref="detailsModal" />
    <StockDeviceServicepartnerShipmentModal ref="shipmentModal" />
    <DeviceSelectModal ref="deviceModal" />
    <StockDeviceHistoryModal ref="historyModal" />
    <DeviceImeiHistoryModal ref="imeiHistoryModal" />
    <ImeiInformationModal ref="imeiInformationModal" />
    <StockDeviceClosingModal ref="closingModal" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import { useRoute } from 'vue-router';
import TopNav from '../components/TopNav.vue';
import AlertModal from '../components/AlertModal.vue';
import ConfirmModal from '../components/ConfirmModal.vue';
import DeviceImeiHistoryModal from '../components/DeviceImeiHistoryModal.vue';
import ImeiInformationModal from '../components/ImeiInformationModal.vue';
import StockDeviceDetailsModal from '../components/StockDeviceDetailsModal.vue';
import StockDeviceServicepartnerShipmentModal from '../components/StockDeviceServicepartnerShipmentModal.vue';
import StockDeviceHistoryModal from '../components/StockDeviceHistoryModal.vue';
import StockDeviceClosingModal from '../components/StockDeviceClosingModal.vue';
import DeviceSelectModal from '../components/DeviceSelectModal.vue';
import PartnerOutcomeBadge from '../components/PartnerOutcomeBadge.vue';
import {
  list,
  correctDevice,
  deleteServicepartnerShipment,
  reprintServicepartnerShipmentLabel,
  getImeiInformation,
  fetchImeiInformation,
  resumeHsReceipt,
  ApiError,
  STOCK_STATUS_ABGESCHLOSSEN,
  PARTNER_OUTCOME_STATUS_IDS,
  type StockDevice,
  type StockDeviceClosingResult,
} from '../services/stockDevices';

const items      = ref<StockDevice[]>([]);
const loading    = ref(true);
const loadError  = ref<string | null>(null);

const searchValue = ref('');
const searchTerm  = computed(() => searchValue.value.trim().toLowerCase());

const scanInput      = useTemplateRef<HTMLInputElement>('scanInput');
const alertModal     = useTemplateRef<InstanceType<typeof AlertModal>>('alertModal');
const confirmModal   = useTemplateRef<InstanceType<typeof ConfirmModal>>('confirmModal');
// Der Hinweiskasten dient zwei Zwecken (Lieferschein, Versand) — Titel je Anlass.
const alertTitle     = ref('Lieferschein erstellen');
const detailsModal   = useTemplateRef<InstanceType<typeof StockDeviceDetailsModal>>('detailsModal');
const shipmentModal   = useTemplateRef<InstanceType<typeof StockDeviceServicepartnerShipmentModal>>('shipmentModal');
const deviceModal    = useTemplateRef<InstanceType<typeof DeviceSelectModal>>('deviceModal');
const historyModal   = useTemplateRef<InstanceType<typeof StockDeviceHistoryModal>>('historyModal');
const imeiHistoryModal = useTemplateRef<InstanceType<typeof DeviceImeiHistoryModal>>('imeiHistoryModal');
const imeiInformationModal = useTemplateRef<InstanceType<typeof ImeiInformationModal>>('imeiInformationModal');
const closingModal   = useTemplateRef<InstanceType<typeof StockDeviceClosingModal>>('closingModal');
const route = useRoute();
// Das Bestätigungsmodal dient zwei Anlässen (Storno, Korrektur) — Titel und
// Beschriftung je Anlass.
const confirmTitle   = ref('Versand stornieren');
const confirmLabel   = ref('Stornieren');

type SortKey = 'stock_number' | 'vendor' | 'master' | 'condition';

const SORTABLE_COLUMNS: Array<{ key: SortKey; label: string; width?: string }> = [
  { key: 'stock_number', label: 'R2-Nr',     width: '110px' },
  { key: 'vendor',       label: 'Hersteller' },
  // Gerät, Speicher und Farbe in einer Spalte — sortiert wird nach der ganzen Zeile.
  { key: 'master',       label: 'Gerät' },
  { key: 'condition',    label: 'Zustand' },
];

// Standard: nach Gerätebezeichnung („iPhone 16 Pro Max, 512 GB, Desert Titanium"),
// damit gleiche Geräte im Lager beieinanderstehen.
const sortKey = ref<SortKey>('master');
const sortAsc = ref(true);

const showClosed = ref(false);
const onlyLegacy = ref(false);

/** Lagerliste ohne abgeschlossene Geräte — außer der Schalter ist an. Optional nur Altgeräte. */
const visibleItems = computed<StockDevice[]>(() => items.value.filter(d =>
  (showClosed.value || !isClosed(d)) && (!onlyLegacy.value || d.is_legacy_device)));

/**
 * Suchtreffer, die nur wegen des Schalters fehlen. Sonst sucht jemand nach einem
 * abgeschlossenen Gerät und hält es für verschwunden.
 */
const hiddenClosedHits = computed<number>(() => showClosed.value || searchTerm.value === ''
  ? 0
  : items.value.filter(d => isClosed(d) && (!onlyLegacy.value || d.is_legacy_device)
      && haystack(d).includes(searchTerm.value)).length);

const hiddenClosedLabel = computed<string>(() => hiddenClosedHits.value === 1
  ? '1 abgeschlossenes Gerät ausgeblendet'
  : `${hiddenClosedHits.value} abgeschlossene Geräte ausgeblendet`);

function isClosed(device: StockDevice): boolean {
  return device.status.id === STOCK_STATUS_ABGESCHLOSSEN;
}

const sortedItems = computed<StockDevice[]>(() => {
  const list = searchTerm.value === ''
    ? [...visibleItems.value]
    : visibleItems.value.filter(d => haystack(d).includes(searchTerm.value));
  const dir  = sortAsc.value ? 1 : -1;
  return list.sort((a, b) => compare(a, b) * dir || a.id - b.id);
});

function compare(a: StockDevice, b: StockDevice): number {
  switch (sortKey.value) {
    // Sortiert nach der angezeigten Nummer: Ticket-Nummer, sonst die eigene id.
    case 'stock_number': return (a.ticket?.id ?? a.id) - (b.ticket?.id ?? b.id);
    // Zustand nach display_order der Stammdaten, nicht alphabetisch.
    case 'condition':    return (a.condition?.sort_order ?? 0) - (b.condition?.sort_order ?? 0);
    case 'vendor':       return text(a.vendor.description, b.vendor.description);
    // Nach der ganzen angezeigten Bezeichnung inkl. Speicher und Farbe — so steht
    // dasselbe Gerät in derselben Variante zusammen.
    case 'master':       return text(deviceLabel(a), deviceLabel(b));
  }
}

/**
 * Worüber gesucht wird. Die R2-Nummer steht mit und ohne Präfix drin, damit sowohl
 * der Scan („R2-5432") als auch die getippte Nummer („5432") trifft.
 */
function haystack(device: StockDevice): string {
  return [
    device.stock_number,
    device.stock_number.replace(/^(R2|RD)-/, ''),
    device.imei_sn ?? '',
    device.vendor.description,
    // Die Bezeichnung eines Altgeräts ist bewusst nicht durchsuchbar (Entscheidung
    // 22.10.2026) — Altgeräte findet man über den Filter „Nur Altgeräte".
    device.is_legacy_device ? '' : deviceLabel(device),
    device.condition?.description ?? '',
    device.status.description,
    // Die Referenznummer des Partners: danach sucht man, wenn er anruft und sie nennt.
    device.partner_case?.partner_reference ?? '',
  ].join(' ').toLowerCase();
}

/**
 * Gerät, Speicher und Farbe in einer Zeile — wie auf dem Lieferschein. Wird für
 * Sortierung und Suche gebraucht: dort zählt die ganze Bezeichnung, damit
 * dasselbe Gerät in derselben Variante zusammensteht.
 */
function deviceLabel(device: StockDevice): string {
  return [device.master.description, device.memory_size_label, device.color?.description ?? '']
    .filter(part => (part ?? '').trim() !== '')
    .join(', ');
}

/** Nur Speicher und Farbe — der Master steht daneben als Link. */
function variantLabel(device: StockDevice): string {
  return [device.memory_size_label, device.color?.description ?? '']
    .filter(part => (part ?? '').trim() !== '')
    .join(', ');
}

function text(a: string, b: string): number {
  return a.localeCompare(b, 'de', { sensitivity: 'base' });
}

/** Gleiche Spalte nochmal = Richtung umdrehen, neue Spalte startet aufsteigend. */
function sortBy(key: SortKey): void {
  if (sortKey.value === key) {
    sortAsc.value = !sortAsc.value;
    return;
  }
  sortKey.value = key;
  sortAsc.value = true;
}

onMounted(async () => {
  // `?q=` belegt die Suche vor - so fuehrt die R2-Nummer aus dem Dashboard
  // (Preisanfragen) direkt auf das Geraet.
  const q = route.query.q;
  if (typeof q === 'string' && q.trim() !== '') searchValue.value = q.trim();
  await reload();
  scanInput.value?.focus();
});

function statusStyle(device: StockDevice): Record<string, string> | undefined {
  if (!device.status.background_color && !device.status.foreground_color) return undefined;
  return {
    background: device.status.background_color || 'var(--accent-soft, rgba(247, 166, 0, 0.10))',
    color:      device.status.foreground_color || 'var(--accent-hover, #e09600)',
  };
}

/** Nur das Datum - im Badge ist fuer die Uhrzeit kein Platz. */
function formatDay(value: string): string {
  const d = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(d);
}

/**
 * IMEI-Auskunft zum Lagergeraet - dasselbe Modal wie am Ticket, nur mit den
 * Ladern des Lagergeraets. Danach die Knopfbeschriftung nachziehen.
 */
async function showImeiInformation(device: StockDevice): Promise<void> {
  const has = await imeiInformationModal.value?.open({
    get:   () => getImeiInformation(device.id),
    fetch: () => fetchImeiInformation(device.id),
  });
  if (has === true) device.has_imei_information = true;
}

/**
 * Gefiltert wird schon beim Tippen, das Enter des Handscanners muss also nichts
 * suchen. Es markiert nur den Text, damit der nächste Scan ihn überschreibt statt
 * anzuhängen — sonst steht nach zwei Scans „R2-1R2-2" im Feld.
 */
function onScanEnter(): void {
  scanInput.value?.select();
}

function clearSearch(): void {
  searchValue.value = '';
  scanInput.value?.focus();
}

async function reload(): Promise<void> {
  loading.value   = true;
  loadError.value = null;
  try {
    items.value = await list();
  } catch (e) {
    loadError.value = e instanceof ApiError ? e.message : 'Gerätelager konnte nicht geladen werden.';
  } finally {
    loading.value = false;
  }
}

/**
 * „Details" zeigt das Gerät und bietet darin seine Aktionen an. Das Detail-Modal ist
 * beim Auswerten schon geschlossen (open() löst erst im hidden-Event auf), damit sich
 * die Backdrops nicht überlagern.
 */
async function onShowDetails(device: StockDevice): Promise<void> {
  const action = await detailsModal.value?.open(device);
  if (action === 'servicepartner') await sendToServicepartner(device);
  else if (action === 'reprint')   await reprintShipmentLabel(device);
  else if (action === 'cancel')    await cancelShipment(device);
  else if (action === 'correct')   await correctStockDevice(device);
  else if (action === 'close')     await closeStockDevice(device);
  else if (action === 'resume-hs-receipt') await resumeStockDeviceHsReceipt(device);
  else if (action === 'history')   await historyModal.value?.open(device);
  else if (action === 'imei-information') await showImeiInformation(device);
  // Erst wenn der Detail-Dialog zu ist — zwei offene Modals lassen einen
  // Backdrop stehen. Deshalb der Weg über die Aktion und nicht direkt.
  else if (action === 'imei-history') await imeiHistoryModal.value?.open(device);
}

/**
 * Korrigiert, was beim Wareneingang falsch erfasst wurde: Master, Farbe,
 * Speichergröße, Zustand. Geändert wird nur das Lagergerät — das Herkunftsticket
 * bleibt, wie es ist.
 *
 * Gewählt wird mit derselben Maske wie im Ticket, nur ohne den Block „Versand an
 * Servicepartner": der wird im Lager über die eigene Aktion ausgelöst.
 *
 * Was fachlich nicht geht — ein Masterwechsel, während ein Versand vorbereitet ist —
 * lehnt der Server im Klartext ab; die Meldung wird unverändert angezeigt.
 */
async function correctStockDevice(device: StockDevice): Promise<void> {
  const result = await deviceModal.value?.open(
    {
      device_id:    device.master.id,
      color_id:     device.color?.id ?? null,
      memory_size:  device.memory_size,
      condition_id: device.condition?.id ?? null,
      planned_servicepartner_location_id: null,
      planned_error_pattern_id:           null,
      is_closed:    false,
      alternative_device_description: device.is_legacy_device ? device.alternative_device_description : null,
    },
    {
      title: `Gerät korrigieren — ${device.stock_number}`,
      hint:  'Richtigstellen, was beim Wareneingang falsch erfasst wurde. Geändert wird nur das '
        + 'Lagergerät, das zugehörige Ticket bleibt unverändert. IMEI/SN lässt sich hier nicht ändern. '
        + 'Der Wechsel zwischen Altgerät und Master ist gesperrt, solange ein Versand vorbereitet ist.',
      showPlannedShipment: false,
    },
  );
  if (!result) return;

  // Das Lieferschein-PDF wird bei jedem Aufruf neu erzeugt und zeigt danach die
  // korrigierte Bezeichnung — das gedruckte Papier im Paket nicht.
  if (device.delivery_slips.length > 0) {
    confirmTitle.value = 'Gerät korrigieren';
    confirmLabel.value = 'Korrigieren';
    const slips = device.delivery_slips.map(slip => slip.slip_number).join(', ');
    const confirmed = await confirmModal.value?.open(
      `${device.stock_number} steht auf ${slips}. Der Lieferschein zeigt danach die korrigierte `
      + 'Bezeichnung, das bereits gedruckte Papier weicht davon ab. Trotzdem korrigieren?',
    );
    if (!confirmed) return;
  }

  try {
    const corrected = await correctDevice(device.id, {
      device_id:    result.device?.id ?? null,
      color_id:     result.color_id,
      memory_size:  result.memory_size,
      condition_id: result.condition_id,
      alternative_device_description: result.alternative_device_description,
    });
    await reload();
    alertTitle.value = corrected.changed ? 'Gerät korrigiert' : 'Nichts geändert';
    await alertModal.value?.open(corrected.changed
      ? `${device.stock_number}: ${corrected.old_description} → ${corrected.new_description}`
      : `An ${device.stock_number} war nichts zu ändern.`);
  } catch (e) {
    alertTitle.value = 'Korrektur fehlgeschlagen';
    await alertModal.value?.open(e instanceof Error ? e.message : 'Das Gerät konnte nicht korrigiert werden.');
  }
}

/**
 * Manueller Abschluss. Das Modal schickt selbst ab und zeigt Ablehnungen des
 * Servers dort an; hier wird nur neu geladen und bestätigt. Das Gerät
 * verschwindet danach aus der Standardansicht.
 *
 * Die Bestätigung geht nach ein paar Sekunden von selbst weg — außer es gibt
 * etwas zu tun (Einbuchung unvollständig, Druckproblem). Danach steht der Cursor
 * wieder im Suchfeld, bereit für den nächsten Scan.
 */
async function closeStockDevice(device: StockDevice): Promise<void> {
  const result = await closingModal.value?.open(device);
  if (!result) {
    scanInput.value?.focus();
    return;
  }
  await reload();
  const needsAttention = !!result.incomplete_error || !!result.print_warning;
  alertTitle.value = result.incomplete_error ? `${bookingLabel(result)} unvollständig` : 'Gerät abgeschlossen';
  await alertModal.value?.open(`${device.stock_number} ist abgeschlossen`
    + (result.cancelled_shipments > 0 ? ', der vorbereitete Versand wurde storniert.' : '.')
    + hsReceiptMessage(result), needsAttention ? undefined : 3000);
  scanInput.value?.focus();
}

/**
 * Abgebrochene Buchung „Lagereingang HS" bzw. „Ankauf HS.cc" fortsetzen (aus dem Detail-Modal).
 * Der Server erkennt erledigte Schritte selbst, es wird nichts doppelt gebucht.
 */
async function resumeStockDeviceHsReceipt(device: StockDevice): Promise<void> {
  try {
    const result = await resumeHsReceipt(device.id);
    await reload();
    alertTitle.value = `${bookingLabel(result)} ${result.incomplete_error ? 'unvollständig' : 'abgeschlossen'}`;
    await alertModal.value?.open(`${device.stock_number}:` + hsReceiptMessage(result));
  } catch (e) {
    alertTitle.value = 'Einbuchung fehlgeschlagen';
    await alertModal.value?.open(e instanceof Error ? e.message : 'Die Einbuchung konnte nicht fortgesetzt werden.');
  }
}

function bookingLabel(result: StockDeviceClosingResult): string {
  return result.booking_kind === 'ankauf' ? 'Ankauf' : 'Einbuchung';
}

/** Ergebnis der Buchung „Lagereingang HS" / „Ankauf HS.cc" als Satz — leer bei den übrigen Abschlussarten. */
function hsReceiptMessage(result: StockDeviceClosingResult): string {
  if (result.incomplete_error) {
    const label = bookingLabel(result);
    return ` ${label === 'Ankauf' ? 'Der Ankauf' : 'Die Einbuchung'} ist unvollständig: ${result.incomplete_error} Im Gerät über „${label} fortsetzen“ nachholen.`;
  }
  if (result.booking_kind === 'ankauf') {
    if (!result.invoice_number) return '';
    return ` Ankaufsrechnung ${result.invoice_number} an Assona erstellt, Ankaufsartikel ${result.e_number ?? ''},`
      + ` Lieferscheine ${(result.hs_delivery_slip_numbers ?? []).join(' → ')} an den Ankauf.`
      + (result.print_warning ? ` Druck: ${result.print_warning}` : ` Das Etikett mit der E-Nummer kommt auf deinem Etikettendrucker.`);
  }
  if (!result.hs_delivery_slip_number) return '';
  return ` Eingebucht bei Repaircenter (Bestellung #${result.supplier_order_id}), Lieferschein ${result.hs_delivery_slip_number} an die HS Zentrale erstellt.`
    + (result.print_warning ? ` Druck: ${result.print_warning}` : '');
}

/**
 * Nachdruck des Etiketts. Ändert nichts am Gerät, deshalb auch kein Neuladen — nur
 * die Rückmeldung, ob das Papier rauskam oder in der Warteschlange liegt.
 */
async function reprintShipmentLabel(device: StockDevice): Promise<void> {
  try {
    const result = await reprintServicepartnerShipmentLabel(device.id);
    alertTitle.value = result.printed ? 'Etikett nachgedruckt' : 'Etikett nicht gedruckt';
    await alertModal.value?.open(result.printed
      ? `Das Versandetikett für ${device.stock_number} wurde erneut gedruckt.`
      : (result.print_warning ?? 'Der Druckauftrag konnte nicht zugestellt werden.'));
  } catch (e) {
    alertTitle.value = 'Etikett nicht gedruckt';
    // "wrong_status" kommt vom Server, wenn das Geraet schon beim Partner (oder
    // zurueck) ist - die Oberflaeche sperrt den Knopf dann ohnehin.
    const message = e instanceof Error && e.message === 'wrong_status'
      ? 'Das Gerät ist beim Servicepartner angekommen bzw. zurück — das Versandetikett hat keinen Zweck mehr.'
      : (e instanceof Error ? e.message : 'Der Nachdruck ist fehlgeschlagen.');
    await alertModal.value?.open(message);
  }
}

/**
 * Versand stornieren: die vorbereitete Versandzeile wird gelöscht, das Gerät ist
 * damit wieder frei. Wirkt nur vor dem Lieferschein — danach sperrt das Detail-Modal
 * die Aktion, und der Server lehnt sie ebenfalls ab.
 */
async function cancelShipment(device: StockDevice): Promise<void> {
  confirmTitle.value = 'Versand stornieren';
  confirmLabel.value = 'Stornieren';
  const target = device.shipment?.servicepartner.description ?? 'den Servicepartner';
  const confirmed = await confirmModal.value?.open(
    `Vorbereiteten Versand von ${device.stock_number} an ${target} wirklich stornieren? `
    + 'Das Gerät bleibt im Lager und kann danach neu versandt werden.',
  );
  if (!confirmed) return;

  try {
    await deleteServicepartnerShipment(device.id);
    await reload();
    alertTitle.value = 'Versand storniert';
    await alertModal.value?.open(`Der Versand von ${device.stock_number} wurde storniert.`);
  } catch (e) {
    alertTitle.value = 'Storno fehlgeschlagen';
    await alertModal.value?.open(e instanceof Error ? e.message : 'Der Versand konnte nicht storniert werden.');
  }
}

async function sendToServicepartner(device: StockDevice): Promise<void> {
  if (!await shipmentModal.value?.open(device)) return;
  // „Nächstes Ziel" kommt aus dem Versand — ohne Neuladen bliebe die Zeile leer und
  // der Versand sähe aus, als wäre er nicht angekommen. Gilt auch, wenn nur der
  // Druck gescheitert ist: der Versand steht trotzdem.
  await reload();
  // Angelegt, aber nicht gedruckt: der Versand gilt, das Papier holt man aus der
  // Warteschlange. Muss der Bearbeiter sehen — sonst wartet er auf ein Etikett.
  const printWarning = shipmentModal.value?.lastPrintWarning();
  if (printWarning) {
    alertTitle.value = 'Versand angelegt, aber nicht gedruckt';
    await alertModal.value?.open(printWarning);
  }
  // Versand steht, aber Lieferschein oder Übernahme fehlen — dort geht es von Hand weiter.
  const instantWarning = shipmentModal.value?.lastInstantClosingWarning();
  if (instantWarning) {
    alertTitle.value = 'Sofortiger LS-Abschluss nicht vollständig';
    await alertModal.value?.open(instantWarning);
  }
}
</script>

<style scoped>
.dashboard {
  max-width: 1600px;
}

.stock-toolbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
}

.stock-scan {
  min-width: 260px;
}

.stock-show-closed {
  margin-bottom: 6px;
}

.stock-hidden-closed {
  margin-top: 6px;
  font-size: 0.9rem;
  color: var(--bs-secondary-color, #6c757d);
}

.stock-scan-notice {
  margin-top: 4px;
  font-size: 0.9rem;
  color: var(--bs-secondary-color, #6c757d);
}

/* Sitzt in der Trefferzeile, soll die Zeilenhoehe nicht sprengen. */
.stock-scan-clear {
  padding: 0 0 0 6px;
  vertical-align: baseline;
}

.stock-sortable {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.stock-sortable:hover {
  background-color: rgba(0, 0, 0, 0.03);
}

.stock-sort-marker {
  font-size: 0.7em;
  color: var(--bs-secondary-color, #6c757d);
}

.stock-variant {
  color: #6c757d;
}

.stock-number {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}

/* „Versand zu Servicepartner" ist der längste Status und soll in einer Zeile
   stehen — die Spalte richtet sich danach statt umgekehrt. */
.stock-status-cell {
  width: 1%;
  white-space: nowrap;
}

/* Status-Badge - dieselbe Optik wie in der Ticketliste (.assona-status). */
.stock-status {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 9999px;
  background: var(--accent-soft, rgba(247, 166, 0, 0.10));
  color: var(--accent-hover, #e09600);
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

/* Zeitpunkt des letzten Statuswechsels - im selben Badge, aber zurueckgenommen,
   damit der Status die Hauptaussage bleibt. */
.stock-status-when {
  font-weight: 500;
  opacity: 0.75;
  font-variant-numeric: tabular-nums;
}

@media (max-width: 767.98px) {
  .stock-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .table-responsive {
    overflow-x: auto;
  }
}
.stock-partner-outcome {
  margin-left: 0.35rem;
}

.stock-legacy-badge {
  margin-left: 0.35rem;
  font-weight: 500;
}
</style>
